import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import type { ConfirmDraftResult } from '@/lib/draft-order-client';
import { customerCartSummary } from './cart-draft';

interface ConfirmSummaryDialogProps {
  open: boolean;
  result: ConfirmDraftResult | null;
  submitting?: boolean;
  onClose: () => void;
  onSend: (message: string) => void;
}

/**
 * What the customer hears about the order the operator just placed.
 *
 * Confirmation is the moment the tracking URL and the payment link come into existence, and nobody
 * has told the customer anything yet — the agent is paused. Prefilling the message from the
 * confirmation result is what stops an operator from placing an order and silently leaving the
 * customer with no link to pay.
 */
export function ConfirmSummaryDialog({ open, result, submitting, onClose, onSend }: ConfirmSummaryDialogProps) {
  const { t } = useTranslation('conversations');
  const [message, setMessage] = useState('');
  const [send, setSend] = useState(true);

  useEffect(() => {
    if (!open || !result) return;
    setSend(true);
    setMessage(buildSuggestedMessage(result, t));
  }, [open, result, t]);

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="flex max-h-[85vh] flex-col overflow-hidden sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('cart.confirmed.title')}</DialogTitle>
          <DialogDescription>{t('cart.confirmed.description')}</DialogDescription>
        </DialogHeader>

        <div className="flex-1 space-y-3 overflow-y-auto">
          {result?.paymentLinkUrl && (
            <p className="rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-700 dark:border-blue-800/60 dark:bg-blue-950/40 dark:text-blue-300">
              {t('cart.confirmed.paymentPending')}
            </p>
          )}

          <div className="flex items-center gap-2">
            <Checkbox id="cart-send-summary" checked={send} onCheckedChange={(next) => setSend(next === true)} />
            <Label htmlFor="cart-send-summary" className="text-sm font-normal">
              {t('cart.confirmed.sendToCustomer')}
            </Label>
          </div>

          <Textarea rows={8} value={message} disabled={!send} onChange={(event) => setMessage(event.target.value)} />
        </div>

        <DialogFooter className="gap-2">
          <Button type="button" variant="outline" onClick={onClose} disabled={submitting}>
            {t('cart.confirmed.close')}
          </Button>
          <Button
            type="button"
            loading={submitting}
            disabled={!send || !message.trim()}
            onClick={() => onSend(message.trim())}
          >
            <Send className="h-3.5 w-3.5" />
            {t('cart.confirmed.send')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function buildSuggestedMessage(
  result: ConfirmDraftResult,
  t: ReturnType<typeof useTranslation<'conversations'>>['t'],
): string {
  const summary = customerCartSummary(result.cart, {
    deliveryFee: t('cart.totals.delivery'),
    tip: t('cart.totals.tip'),
    freeDelivery: t('cart.totals.freeDelivery'),
  });
  const parts = [t('cart.confirmed.messageIntro'), '', summary];

  if (result.trackingUrl) {
    parts.push('', t('cart.confirmed.messageTracking', { url: result.trackingUrl }));
  }
  if (result.paymentLinkUrl) {
    parts.push('', t('cart.confirmed.messagePaymentLink', { url: result.paymentLinkUrl }));
    if (result.paymentDeadlineAt) {
      parts.push(
        t('cart.confirmed.messagePaymentDeadline', {
          time: new Date(result.paymentDeadlineAt).toLocaleTimeString(undefined, {
            hour: '2-digit',
            minute: '2-digit',
          }),
        }),
      );
    }
  }

  return parts.join('\n');
}
